import { useEffect, useRef, useState } from "react";
import { Clapperboard, Trash2, Upload, Play } from "lucide-react";
import { MediaSkeleton } from "../components/Skeleton.jsx";
import {
  uploadWorkerWorkSample,
  deleteWorkerWorkSample,
  getWorkerWorkSampleBlob,
} from "./workerDetailedProfileApi.js";

function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return null;
  }
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

function formatSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return null;
  }
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

/**
 * P158: one work sample. Loads on demand - a list of five clips must not pull
 * five videos down the moment the tab opens, so nothing is fetched until the
 * operator presses play on that specific one.
 */
function WorkSamplePlayer({ applicationId, sample }) {
  const [state, setState] = useState({ status: "idle", objectUrl: null });

  useEffect(
    () => () => {
      if (state.objectUrl) {
        URL.revokeObjectURL?.(state.objectUrl);
      }
    },
    [state.objectUrl],
  );

  async function load() {
    setState({ status: "loading", objectUrl: null });
    try {
      const { blob } = await getWorkerWorkSampleBlob(applicationId, sample.workSampleId);
      setState({ status: "ready", objectUrl: URL.createObjectURL(blob) });
    } catch {
      setState({ status: "error", objectUrl: null });
    }
  }

  if (state.status === "ready") {
    // eslint-disable-next-line jsx-a11y/media-has-caption
    return <video className="wx-wdp-ws-video" src={state.objectUrl} controls autoPlay preload="metadata" />;
  }

  return (
    <button type="button" className="wx-wdp-ws-poster" onClick={load} disabled={state.status === "loading"}>
      {state.status === "loading" ? (
        <MediaSkeleton label="กำลังโหลดวิดีโอ..." height="100%" radius={8} />
      ) : state.status === "error" ? (
        <span className="wx-wdp-ws-error">โหลดวิดีโอไม่สำเร็จ กดเพื่อลองใหม่</span>
      ) : (
        <>
          <Play size={22} aria-hidden="true" />
          <span>เล่นวิดีโอ</span>
        </>
      )}
    </button>
  );
}

/**
 * P158: the worker's portfolio, as video.
 *
 * An employer hiring a construction worker judges the work by watching it, so
 * this is the one place in the product that takes video. Uploading reads the
 * clip's real duration in the browser first (see below) and sends it along; a
 * clip whose length cannot be read is stored with no duration rather than a
 * made-up one.
 */
export default function WorkSamples({ applicationId, workSamples, canEdit, onChanged }) {
  const inputRef = useRef(null);
  const [title, setTitle] = useState("");
  const [state, setState] = useState({ status: "idle", message: null });
  const [busyId, setBusyId] = useState(null);

  /**
   * Reads the duration off the file before upload, using a detached <video>.
   * Resolves null rather than rejecting: an unreadable container is a perfectly
   * ordinary outcome and must not block the upload itself.
   */
  function readDuration(file) {
    return new Promise((resolve) => {
      if (typeof document === "undefined" || typeof URL.createObjectURL !== "function") {
        resolve(null);
        return;
      }
      const url = URL.createObjectURL(file);
      const probe = document.createElement("video");
      const done = (value) => {
        URL.revokeObjectURL?.(url);
        resolve(value);
      };
      probe.preload = "metadata";
      probe.onloadedmetadata = () => done(Number.isFinite(probe.duration) ? probe.duration : null);
      probe.onerror = () => done(null);
      probe.src = url;
      // A container the browser cannot parse fires neither event, so the probe
      // needs its own deadline or the upload would hang on it. 2s is generous
      // for reading a local file header and short enough that a worker with an
      // odd clip is not left staring at a stalled button. (jsdom never fires
      // either event, so this path is also what the tests take.)
      setTimeout(() => done(null), 2000);
    });
  }

  async function handleFile(event) {
    const file = event.target.files?.[0];
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    if (!file) {
      return;
    }
    setState({ status: "uploading", message: null });
    try {
      const durationSeconds = await readDuration(file);
      const updated = await uploadWorkerWorkSample(applicationId, file, { title: title.trim(), durationSeconds });
      onChanged?.(updated);
      setTitle("");
      setState({ status: "idle", message: null });
    } catch (error) {
      setState({
        status: "error",
        message: error?.body?.message ?? "อัปโหลดวิดีโอไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
      });
    }
  }

  async function handleDelete(workSampleId) {
    setBusyId(workSampleId);
    setState({ status: "idle", message: null });
    try {
      const updated = await deleteWorkerWorkSample(applicationId, workSampleId);
      onChanged?.(updated);
    } catch (error) {
      setState({
        status: "error",
        message: error?.body?.message ?? "ลบวิดีโอไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
      });
    } finally {
      setBusyId(null);
    }
  }

  const rows = workSamples ?? [];

  return (
    <div className="wx-wdp-ws">
      <h4 className="wx-wdp-group">
        <Clapperboard size={16} aria-hidden="true" />
        <span>วิดีโอผลงาน</span>
        {rows.length > 0 && <span className="wx-wdp-count">{rows.length}</span>}
      </h4>

      <p className="wx-wdp-ws-note">
        นายจ้างดูวิดีโอเพื่อประเมินฝีมือ แนบคลิปหน้างานได้หลายคลิป (MP4, MOV หรือ WebM ไม่เกิน 256 MB ต่อคลิป)
      </p>

      {rows.length === 0 ? (
        <p className="wx-wdp-repeat-empty">ยังไม่มีวิดีโอผลงาน</p>
      ) : (
        <ul className="wx-wdp-ws-list">
          {rows.map((sample) => (
            <li key={sample.workSampleId} className="wx-wdp-ws-item">
              <WorkSamplePlayer applicationId={applicationId} sample={sample} />
              <div className="wx-wdp-ws-meta">
                <strong>{sample.title || "วิดีโอผลงาน"}</strong>
                <span>
                  {[formatDuration(sample.durationSeconds), formatSize(sample.sizeBytes), sample.fileName]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </div>
              {canEdit && (
                <button
                  type="button"
                  className="wx-wdp-repeat-remove"
                  onClick={() => handleDelete(sample.workSampleId)}
                  disabled={busyId === sample.workSampleId}
                  aria-label={`ลบวิดีโอ ${sample.title || sample.fileName || ""}`}
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div className="wx-wdp-extra-add">
          <label className="wx-wdp-field-control wx-wdp-extra-type">
            <span className="wx-wdp-field-label">ชื่อผลงาน</span>
            <input
              type="text"
              maxLength={200}
              placeholder="เช่น ฉาบปูนผนัง, ผูกเหล็กเสา"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="wx-button wx-button--ghost"
            onClick={() => inputRef.current?.click()}
            disabled={state.status === "uploading"}
          >
            <Upload size={15} aria-hidden="true" />
            {state.status === "uploading" ? "กำลังอัปโหลด..." : "แนบวิดีโอผลงาน"}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm"
            className="wx-wdp-doc-input"
            aria-label="เลือกไฟล์วิดีโอผลงาน"
            onChange={handleFile}
          />
        </div>
      )}

      {state.status === "error" && (
        <p className="wx-wdp-doc-error" role="alert">
          {state.message}
        </p>
      )}
    </div>
  );
}
